/*
 * Cart Gift Picker
 * Ajoute / retire un produit offert (propriété de ligne `_gift`) depuis la page
 * panier OU le tiroir panier, puis rafraîchit les sections concernées via l'API
 * Section Rendering.
 */
if (!customElements.get('cart-gift-picker')) {
  class CartGiftPicker extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      }
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsub) this.cartUpdateUnsub();
    }

    get isDrawer() {
      return this.dataset.context === 'drawer' || !!this.closest('cart-drawer');
    }

    get sectionId() {
      return this.dataset.sectionId;
    }

    get giftLimit() {
      return parseInt(this.dataset.giftLimit || '1', 10);
    }

    get giftKeys() {
      return (this.dataset.giftKeys || '').split(',').filter(Boolean);
    }

    onCartUpdate(event) {
      if (event && event.source === 'cart-gift-picker') return;
      // En mode tiroir, le drawer se re-render tout seul (et recrée cet élément).
      if (this.isDrawer) return;
      this.refresh();
    }

    onClick(event) {
      const btn = event.target.closest('.cart-gift__choose');
      if (!btn || btn.disabled) return;
      event.preventDefault();

      const card = btn.closest('.cart-gift__card');
      if (!card) return;

      if (card.classList.contains('is-selected')) {
        this.removeGifts(btn);
        return;
      }

      const select = card.querySelector('.cart-gift__size');
      const variantId = select ? select.value : card.dataset.defaultVariant;
      if (!variantId) return;
      this.addGift(variantId, btn);
    }

    sectionsList() {
      if (this.isDrawer) {
        return ['cart-drawer', 'cart-icon-bubble'];
      }
      const ids = [];
      const items = document.getElementById('main-cart-items');
      const footer = document.getElementById('main-cart-footer');
      if (items && items.dataset.id) ids.push(items.dataset.id);
      if (footer && footer.dataset.id) ids.push(footer.dataset.id);
      ids.push('cart-icon-bubble');
      if (this.sectionId) ids.push(this.sectionId);
      return ids;
    }

    requestConfig(body) {
      const base =
        typeof fetchConfig === 'function'
          ? fetchConfig()
          : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      if (base.headers && base.headers['X-Requested-With'] === undefined) {
        base.headers['X-Requested-With'] = 'XMLHttpRequest';
      }
      return { ...base, body: JSON.stringify(body) };
    }

    async addGift(variantId, btn) {
      this.setBusy(btn, true);
      this.clearError();
      try {
        if (this.giftKeys.length >= this.giftLimit) {
          for (const key of this.giftKeys) {
            await fetch(`${routes.cart_change_url}`, this.requestConfig({ id: key, quantity: 0 }));
          }
        }

        const res = await fetch(
          `${routes.cart_add_url}`,
          this.requestConfig({
            items: [{ id: Number(variantId), quantity: 1, properties: { _gift: 'true' } }],
            sections: this.sectionsList(),
            sections_url: window.location.pathname,
          })
        );
        const data = await res.json();

        if (data.status) {
          this.showError(data.description);
          return;
        }

        this.renderFromSections(data.sections);
        this.publishCartUpdate();
      } catch (e) {
        console.error('[cart-gift-picker]', e);
        this.showError();
      } finally {
        this.setBusy(btn, false);
      }
    }

    async removeGifts(btn) {
      this.setBusy(btn, true);
      this.clearError();
      try {
        const keys = this.giftKeys;
        let data = null;

        for (let i = 0; i < keys.length; i += 1) {
          const isLast = i === keys.length - 1;
          const body = isLast
            ? {
                id: keys[i],
                quantity: 0,
                sections: this.sectionsList(),
                sections_url: window.location.pathname,
              }
            : { id: keys[i], quantity: 0 };
          const res = await fetch(`${routes.cart_change_url}`, this.requestConfig(body));
          data = await res.json();
        }

        if (data && data.sections) {
          this.renderFromSections(data.sections);
        } else if (!this.isDrawer) {
          await this.refresh();
        }
        this.publishCartUpdate();
      } catch (e) {
        console.error('[cart-gift-picker]', e);
        this.showError();
      } finally {
        this.setBusy(btn, false);
      }
    }

    publishCartUpdate() {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      // En mode tiroir, on émet avec la source 'cart-items' : tous les abonnés Dawn
      // (CartItems / CartDrawerItems) ignorent cette source et ne re-render pas
      // par-dessus notre rendu — on a déjà rafraîchi le tiroir et l'icône panier.
      const source = this.isDrawer ? 'cart-items' : 'cart-gift-picker';
      publish(PUB_SUB_EVENTS.cartUpdate, { source });
    }

    renderFromSections(sections) {
      if (!sections) return;

      if (this.isDrawer) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          drawer.classList.remove('is-empty');
          const innerSource = new DOMParser()
            .parseFromString(sections['cart-drawer'], 'text/html')
            .querySelector('.drawer__inner');
          const innerTarget = document.querySelector('#CartDrawer .drawer__inner');
          if (innerSource && innerTarget) {
            innerTarget.classList.remove('is-empty');
            innerTarget.innerHTML = innerSource.innerHTML;
          }
        }
        this.replaceCartIcon(sections['cart-icon-bubble']);
        return;
      }

      const cartItemsEl = document.getElementById('main-cart-items');
      const cartFooterEl = document.getElementById('main-cart-footer');

      const map = [
        ['main-cart-items', cartItemsEl && cartItemsEl.dataset.id, '.js-contents'],
        ['main-cart-footer', cartFooterEl && cartFooterEl.dataset.id, '.js-contents'],
      ];

      map.forEach(([elId, secId, selector]) => {
        if (!secId || !sections[secId]) return;
        const container = document.getElementById(elId);
        if (!container) return;
        const target = container.querySelector(selector) || container;
        target.innerHTML = this.getInnerHTML(sections[secId], selector);
      });

      this.replaceCartIcon(sections['cart-icon-bubble']);

      if (this.sectionId && sections[this.sectionId]) {
        this.replaceSelf(sections[this.sectionId]);
      }
    }

    replaceCartIcon(html) {
      if (!html) return;
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;
      const target = bubble.querySelector('.shopify-section') || bubble;
      target.innerHTML = this.getInnerHTML(html, '.shopify-section');
    }

    async refresh() {
      try {
        const res = await fetch(`${routes.cart_url}?section_id=${this.sectionId}`);
        const text = await res.text();
        this.replaceSelf(text);
      } catch (e) {
        console.error('[cart-gift-picker]', e);
      }
    }

    replaceSelf(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('cart-gift-picker');
      if (!fresh) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      this.innerHTML = fresh.innerHTML;
      ['giftKeys', 'giftCount', 'unlocked'].forEach((key) => {
        this.dataset[key] = fresh.dataset[key] || '';
      });
      this.className = fresh.className;
      if (fresh.getAttribute('style')) this.setAttribute('style', fresh.getAttribute('style'));
    }

    getInnerHTML(html, selector) {
      const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return el ? el.innerHTML : '';
    }

    setBusy(btn, state) {
      this.classList.toggle('cart-gift--busy', state);
      if (!btn) return;
      const label = btn.querySelector('.cart-gift__choose-label');
      const spinner = btn.querySelector('.cart-gift__spinner');
      btn.disabled = state;
      if (label) label.style.visibility = state ? 'hidden' : '';
      if (spinner) spinner.hidden = !state;
    }

    clearError() {
      const el = this.querySelector('.cart-gift__error');
      if (el) {
        el.hidden = true;
        el.textContent = '';
      }
    }

    showError(message) {
      const el = this.querySelector('.cart-gift__error');
      if (el) {
        el.textContent = message || 'Une erreur est survenue. Réessaie.';
        el.hidden = false;
      }
    }
  }

  customElements.define('cart-gift-picker', CartGiftPicker);
}
